import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  message,
  Row,
  Col,
  Switch,
  Tag,
  Radio,
  Space,
  Tabs
} from 'antd';
import {
  LockOutlined,
  UserOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  BellOutlined,
  DollarOutlined,
  MoonOutlined,
  SunOutlined,
  DesktopOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { stationOwnerAPI } from '../services/api';
import SystemSettings from './SystemSettings';
import { Select } from 'antd';

const { Title, Text } = Typography;

const Settings: React.FC = () => {
  const { stationOwner } = useAuth();
  const { themeMode, setThemeMode } = useTheme();
  const { currentLanguage, changeLanguage, t } = useLanguage();
  const [passwordForm] = Form.useForm();
  const [profileForm] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Sistem ayarları state'leri - localStorage'dan yükle
  const [emailNotifications, setEmailNotifications] = useState(() => {
    const saved = localStorage.getItem('emailNotifications');
    return saved ? JSON.parse(saved) : true;
  });
  const [campaignNotifications, setCampaignNotifications] = useState(() => {
    const saved = localStorage.getItem('campaignNotifications');
    return saved ? JSON.parse(saved) : true;
  });
  const [autoRenewal, setAutoRenewal] = useState(() => {
    const saved = localStorage.getItem('autoRenewal');
    return saved ? JSON.parse(saved) : false;
  });

  const handlePasswordChange = async (values: any) => {
    setLoading(true);
    try {
      await stationOwnerAPI.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });
      message.success('Şifre başarıyla değiştirildi');
      passwordForm.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Şifre değiştirilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (values: any) => {
    setLoading(true);
    try {
      await stationOwnerAPI.updateProfile({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        companyName: values.companyName
      });
      message.success('Profil bilgileri güncellendi');
      // AuthContext'i güncelle
      window.location.reload();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Profil güncellenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSystemSettingsSave = () => {
    try {
      // Ayarları localStorage'a kaydet
      localStorage.setItem('emailNotifications', JSON.stringify(emailNotifications));
      localStorage.setItem('campaignNotifications', JSON.stringify(campaignNotifications));
      localStorage.setItem('autoRenewal', JSON.stringify(autoRenewal));
      
      message.success('Ayarlar kaydedildi');
    } catch (error) {
      message.error('Ayarlar kaydedilemedi');
    }
  };

  const tabItems = [
    {
      key: 'profile',
      label: (
        <span>
          <UserOutlined />
          Profil
        </span>
      ),
      children: (
        <>
          <Row gutter={[16, 16]}>
        {/* Profil Bilgileri */}
        <Col xs={24} lg={12}>
          <Card title="Profil Bilgileri">
            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleProfileUpdate}
              initialValues={{
                firstName: stationOwner?.firstName,
                lastName: stationOwner?.lastName,
                email: stationOwner?.email,
                companyName: stationOwner?.companyName,
              }}
            >
              <Form.Item
                name="firstName"
                label="Ad"
                rules={[{ required: true, message: 'Ad gerekli' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="Adınızı girin" />
              </Form.Item>
              <Form.Item
                name="lastName"
                label="Soyad"
                rules={[{ required: true, message: 'Soyad gerekli' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="Soyadınızı girin" />
              </Form.Item>
              <Form.Item
                name="email"
                label="E-posta"
                rules={[{ required: true, message: 'E-posta gerekli' }, { type: 'email', message: 'Geçerli bir e-posta girin' }]}
              >
                <Input prefix={<MailOutlined />} placeholder="E-posta adresinizi girin" />
              </Form.Item>
              <Form.Item
                name="companyName"
                label="Şirket Adı"
                rules={[{ required: true, message: 'Şirket adı gerekli' }]}
              >
                <Input prefix={<SafetyCertificateOutlined />} placeholder="Şirket adınızı girin" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Profili Güncelle
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Şifre Değiştir */}
        <Col xs={24} lg={12}>
          <Card title="Şifre Değiştir">
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handlePasswordChange}
            >
              <Form.Item
                name="currentPassword"
                label="Mevcut Şifre"
                rules={[{ required: true, message: 'Mevcut şifre gerekli' }]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Mevcut şifrenizi girin" />
              </Form.Item>
              <Form.Item
                name="newPassword"
                label="Yeni Şifre"
                rules={[{ required: true, message: 'Yeni şifre gerekli' }, { min: 6, message: 'Şifre en az 6 karakter olmalı' }]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Yeni şifrenizi girin" />
              </Form.Item>
              <Form.Item
                name="confirmPassword"
                label="Yeni Şifre Tekrar"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Yeni şifreyi tekrar girin' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Şifreler eşleşmiyor!'));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Yeni şifrenizi tekrar girin" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} danger>
                  Şifreyi Değiştir
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        {/* Tema Ayarları */}
        <Col xs={24} lg={12}>
          <Card title="Tema Ayarları">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <Text strong style={{ display: 'block', marginBottom: '8px' }}>Tema Modu</Text>
                <Radio.Group 
                  value={themeMode} 
                  onChange={(e) => setThemeMode(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Radio value="light">
                      <Space>
                        <SunOutlined />
                        <span>Açık Tema</span>
                      </Space>
                    </Radio>
                    <Radio value="dark">
                      <Space>
                        <MoonOutlined />
                        <span>Koyu Tema</span>
                      </Space>
                    </Radio>
                    <Radio value="system">
                      <Space>
                        <DesktopOutlined />
                        <span>Sistem Ayarlarına Uy</span>
                      </Space>
                    </Radio>
                  </Space>
                </Radio.Group>
              </div>
            </div>
          </Card>
        </Col>

        {/* Dil Ayarları */}
        <Col xs={24} lg={12}>
          <Card title={t('language.title')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <Text strong style={{ display: 'block', marginBottom: '8px' }}>{t('language.selectLanguage')}</Text>
                <Select
                  value={currentLanguage}
                  onChange={(lang) => {
                    changeLanguage(lang);
                    message.success(t('language.languageChanged'));
                    setTimeout(() => {
                      window.location.reload();
                    }, 1000);
                  }}
                  style={{ width: '100%' }}
                  size="large"
                >
                  <Select.Option value="tr">
                    <Space>
                      <span>🇹🇷</span>
                      <span>{t('language.turkish')}</span>
                    </Space>
                  </Select.Option>
                  <Select.Option value="en">
                    <Space>
                      <span>🇬🇧</span>
                      <span>{t('language.english')}</span>
                    </Space>
                  </Select.Option>
                </Select>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                  {t('language.restartRequired')}
                </Text>
              </div>
            </div>
          </Card>
        </Col>

        {/* Bildirim Ayarları */}
        <Col xs={24} lg={12}>
          <Card title="Bildirim Ayarları">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Row align="middle" justify="space-between">
                <Col>
                  <Text strong><BellOutlined style={{ marginRight: '8px' }} />E-posta Bildirimleri</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>Sistem bildirimleri e-posta ile gönderilsin mi?</Text>
                </Col>
                <Col>
                  <Switch checked={emailNotifications} onChange={setEmailNotifications} />
                </Col>
              </Row>
              <Row align="middle" justify="space-between">
                <Col>
                  <Text strong><BellOutlined style={{ marginRight: '8px' }} />Kampanya Bildirimleri</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>Kampanya onayları ve güncellemeleri hakkında bildirim alın.</Text>
                </Col>
                <Col>
                  <Switch checked={campaignNotifications} onChange={setCampaignNotifications} />
                </Col>
              </Row>
              <Row align="middle" justify="space-between">
                <Col>
                  <Text strong><DollarOutlined style={{ marginRight: '8px' }} />Otomatik Yenileme</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>Abonelik otomatik olarak yenilensin mi?</Text>
                </Col>
                <Col>
                  <Switch checked={autoRenewal} onChange={setAutoRenewal} />
                </Col>
              </Row>
              <Button type="primary" onClick={handleSystemSettingsSave} style={{ marginTop: '16px' }} loading={loading}>
                Ayarları Kaydet
              </Button>
            </div>
          </Card>
        </Col>

        {/* Hesap Bilgileri */}
        <Col xs={24} lg={12}>
          <Card title="Hesap Bilgileri">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text type="secondary">Kullanıcı Adı:</Text>
                <br />
                <Text strong>{stationOwner?.firstName} {stationOwner?.lastName}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">E-posta:</Text>
                <br />
                <Text strong>{stationOwner?.email}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Şirket:</Text>
                <br />
                <Text strong>{stationOwner?.companyName}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Abonelik:</Text>
                <br />
                <Tag color="blue">{stationOwner?.subscriptionStatus === 'premium' ? 'Premium Üye' : 'Temel Üye'}</Tag>
              </Col>
              <Col span={12}>
                <Text type="secondary">Durum:</Text>
                <br />
                <Tag color="green">Aktif</Tag>
              </Col>
              <Col span={12}>
                <Text type="secondary">Kayıt Tarihi:</Text>
                <br />
                <Text strong>-</Text>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Abonelik Bilgileri */}
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col span={24}>
          <Card title="Abonelik Bilgileri">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" style={{ background: stationOwner?.subscriptionStatus === 'premium' ? '#f6ffed' : '#f0f0f0' }}>
                  <Text strong>Mevcut Plan</Text>
                  <br />
                  <Tag color={stationOwner?.subscriptionStatus === 'premium' ? 'green' : 'default'}>
                    {stationOwner?.subscriptionStatus === 'premium' ? 'Premium' : 'Temel'}
                  </Tag>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" style={{ background: '#e6f7ff' }}>
                  <Text strong>İstasyon Limiti</Text>
                  <br />
                  <Tag color="blue">{stationOwner?.subscriptionStatus === 'premium' ? 'Sınırsız' : '5 İstasyon'}</Tag>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" style={{ background: '#fff7e6' }}>
                  <Text strong>Kampanya Limiti</Text>
                  <br />
                  <Tag color="orange">{stationOwner?.subscriptionStatus === 'premium' ? 'Sınırsız' : '10 Kampanya'}</Tag>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" style={{ background: '#f6ffed' }}>
                  <Text strong>Destek</Text>
                  <br />
                  <Tag color="green">{stationOwner?.subscriptionStatus === 'premium' ? '7/24 Destek' : 'E-posta Destek'}</Tag>
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
        </>
      )
    },
    {
      key: 'system',
      label: (
        <span>
          <SettingOutlined />
          Sistem Ayarları
        </span>
      ),
      children: <SystemSettings />
    }
  ];

  return (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <Title level={2} style={{ marginBottom: '24px' }}>
        Ayarlar
      </Title>

      <Tabs items={tabItems} />
    </div>
  );
};

export default Settings;
